import { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthToken } from '../features/auth/useAuthToken';
import { useLogout } from '../hooks/useLogout';
import { useUser } from '../features/auth/useUser';
import { useCredits } from '../hooks/useCredits';
import { useUserProfile, useSubmitUserAttribute, useDismissAttributeCollection, useSurveyConfig } from '../hooks/useUserProfile';
import { useGlobalJobPolling } from '../hooks/useGlobalJobPolling';
import { useCanvaResultToast } from '../hooks/useCanvaResultToast';
import { usePartnerBranding } from '../hooks/usePartnerBranding';
import { useImpersonation } from '../features/impersonation/useImpersonation';
import { ImpersonationBanner } from '../features/impersonation/ImpersonationBanner';
import { useMaterialsPanelStore } from '../stores/materialsPanelStore';
import { brandConfig } from '../config/brands';
import { getPendingSurveyQuestion } from '../components/survey/pendingSurveyQuestion';
import MainNav from '../components/navigation/MainNav';
import { ScrollToTop } from '../components/routing/ScrollToTop';
import { NotificationBell } from '../components/notifications/NotificationBell';
// import { MobileNotice } from '../components/common/MobileNotice';
import { MenuContent } from '../components/navigation/MenuContent';
import { ImpersonationOrgSwitcher } from '../features/impersonation/ImpersonationOrgSwitcher';

import { useIsMobile } from '../hooks/useIsMobile';
// 生成キューのみを購読する (Context 4分割後の個別フック)。
import { useGenerationJobs } from '../components/generation/state/jobsContext';
import { NotifyToggleSwitch } from '../components/notifications/NotifyToggleSwitch';
import { AttributeCollectionModal } from '../components/survey/AttributeCollectionModal';
import { ExpressModeModal } from '../components/generation/express/ExpressModeModal';
import { appPath } from '../utils/navigation';
import './AppLayout.css';

const POINTS_VISIBILITY_KEY = 'points_visibility';

export default function AppLayout() {
  const { token } = useAuthToken();
  const { isAdmin, isNewUser, user } = useUser();
  // 公開ホーム (未ログイン閲覧) でも AppLayout はマウントされるため 401 を出さない
  const { data: creditsData } = useCredits({ enabled: !!token });
  const { data: userProfile } = useUserProfile();
  const submitAttributeMutation = useSubmitUserAttribute();
  const dismissAttributesMutation = useDismissAttributeCollection();
  const { currentSession: impersonationSession, end: endImpersonation } = useImpersonation();
  const navigate = useNavigate();
  const location = useLocation();

  // グローバルなジョブポーリング（別ページでも生成完了を検知）
  useGlobalJobPolling();
  // Canva 連携のリダイレクト結果をトーストで通知
  useCanvaResultToast();
  // パートナー管理者が設定した背景画像を適用
  const { bgImageUrl, homeOverlayOpacity, homeOverlayOpacityOther, homeCardOpacity } = usePartnerBranding(Boolean(token));

  // トップページとそれ以外で背景オーバーレイの濃さを切り替え
  // トップは背景画像を活かしてナチュラルに見せたいので薄く、他ページは可読性のため濃いめ。
  // 濃度は org-admin のサイト設定で partner 単位に上書き可能 (トップ NULL=0.2 / 他 NULL=0.6)。
  // 背景画像が無いパートナーのトップだけは暗転を外し、ブランド地色をそのまま見せる
  // (暗転は画像の上で文字を読ませるための層なので、画像が無ければ地色を濁らせるだけ)。
  // トップ以外は生成 UI のダークな下地を作る役割も兼ねているため設定値を維持する。
  useEffect(() => {
    const isHome = location.pathname === '/';
    const configuredHomeOpacity =
      typeof homeOverlayOpacity === 'number' && homeOverlayOpacity >= 0 && homeOverlayOpacity <= 1
        ? homeOverlayOpacity
        : 0.2;
    const homeOpacity = bgImageUrl ? configuredHomeOpacity : 0;
    const otherOpacity =
      typeof homeOverlayOpacityOther === 'number' && homeOverlayOpacityOther >= 0 && homeOverlayOpacityOther <= 1
        ? homeOverlayOpacityOther
        : 0.6;
    document.documentElement.style.setProperty(
      '--bg-overlay-opacity',
      isHome ? String(homeOpacity) : String(otherOpacity),
    );
  }, [location.pathname, bgImageUrl, homeOverlayOpacity, homeOverlayOpacityOther]);

  // ホームのメインカード (.home-gen-card) 不透明度を partner 設定で上書き可能に。
  // NULL のときは CSS 側のフォールバック (0.85) を使わせるため remove する。
  useEffect(() => {
    const root = document.documentElement;
    if (
      typeof homeCardOpacity === 'number' &&
      homeCardOpacity >= 0 &&
      homeCardOpacity <= 1
    ) {
      root.style.setProperty('--home-card-opacity', String(homeCardOpacity));
    } else {
      root.style.removeProperty('--home-card-opacity');
    }
  }, [homeCardOpacity]);
  const { activeJobs, setShowJobQueueModal } = useGenerationJobs();
  // 利用者の操作待ちで止まっているジョブ (paused) はステータスバーに数えない (全編モーションの BGM 確認待ち等)
  const activeJobCount = activeJobs.filter(j => j.status === 'polling' && !j.paused).length;
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [showPoints, setShowPoints] = useState(() => {
    const saved = localStorage.getItem(POINTS_VISIBILITY_KEY);
    return saved === null ? true : saved === 'true';
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const { close } = useMaterialsPanelStore();
  const isMobile = useIsMobile();
  const [isHeaderNarrow, setIsHeaderNarrow] = useState(false);
  const [hideMainNav, setHideMainNav] = useState(false);
  const [isExpressModeOpen, setExpressModeOpen] = useState(false);

  // カスタムイベントで特急モードを開く（ScenarioGenerator等から）
  useEffect(() => {
    const handleOpenExpress = () => setExpressModeOpen(true);
    window.addEventListener('openExpressMode', handleOpenExpress);
    return () => window.removeEventListener('openExpressMode', handleOpenExpress);
  }, []);

  // URLパラメータ ?express=1 or ?express_job=xxx で特急モードを開く
  // ?asin=xxx で商品入力済み状態で起動
  const [initialAsin, setInitialAsin] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('express')) {
      // asin パラメータ（Amazon URL または ASIN）を取得
      const asinParam = params.get('asin') || params.get('amazon_url') || null;
      if (asinParam) {
        setInitialAsin(asinParam);
      }
      setExpressModeOpen(true);
      // パラメータを消してURLをクリーンに保つ
      params.delete('express');
      params.delete('asin');
      params.delete('amazon_url');
      const cleanSearch = params.toString();
      navigate(location.pathname + (cleanSearch ? `?${cleanSearch}` : ''), { replace: true });
    }
    if (params.get('express_job')) {
      // ジョブ復帰: モーダルを開く（URLパラメータはuseExpressMode側で管理）
      setExpressModeOpen(true);
    }
  }, [location.search, navigate]);

  // 属性収集モーダル: 未回答の設問があり、かつスキップしていない場合に表示。
  // 代行中は actor 個人への質問なので表示しない (代行先での作業中に
  // 「あなた本人の属性は?」と聞かれると混乱するため。代行解除後に出る)。
  const attributeCurrentQuestion = impersonationSession ? null : getPendingSurveyQuestion(userProfile);

  // アンケート構成 (選択肢・付与額) はモーダル表示が必要なときだけ取得する。
  const surveyConfigQuery = useSurveyConfig(!!attributeCurrentQuestion);

  // localStorageの変更を監視してポイント表示を更新
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem(POINTS_VISIBILITY_KEY);
      setShowPoints(saved === null ? true : saved === 'true');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('pointsVisibilityChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('pointsVisibilityChanged', handleStorageChange);
    };
  }, []);

  // Admin画面かどうか
  const isAdminPage = location.pathname.startsWith('/admin');

  // 新規ユーザーの場合はオンボーディングページにリダイレクト
  useEffect(() => {
    if (isNewUser && location.pathname !== '/onboarding') {
      navigate('/onboarding', { replace: true });
    }
  }, [isNewUser, location.pathname, navigate]);

  // 組織に所属していないユーザーの場合もオンボーディングにリダイレクト
  useEffect(() => {
    if (user && !user.organization_id && !isNewUser && location.pathname !== '/onboarding') {
      navigate('/onboarding', { replace: true });
    }
  }, [user, isNewUser, location.pathname, navigate]);

  // メニュー領域外をクリックしたときにメニューを閉じる
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const handleLogout = useLogout();

  // ヘッダーの実効幅を計算
  // 右パネルが開くと .app-header に margin-right: var(--panel-width) が付き
  // ヘッダー自体が物理的に縮むため、offsetWidth がそのまま実効幅になる。
  const getEffectiveHeaderWidth = useCallback(() => {
    if (!headerRef.current) return 0;
    return headerRef.current.offsetWidth;
  }, []);

  // ヘッダー幅を監視してポイント表示・ナビの表示/非表示を切り替え
  useEffect(() => {
    if (!headerRef.current || isMobile) return;

    const updateHeaderState = () => {
      const effectiveWidth = getEffectiveHeaderWidth();
      // 700px以下でポイント表示を非表示
      setIsHeaderNarrow(effectiveWidth < 700);
      // 650px以下でナビをハンバーガーに移動
      setHideMainNav(effectiveWidth < 650);
    };

    const observer = new ResizeObserver(() => {
      updateHeaderState();
    });

    // CSS変数の変更も監視（MutationObserver）
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'style') {
          updateHeaderState();
        }
      }
    });

    observer.observe(headerRef.current);
    mutationObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

    // 初回実行
    updateHeaderState();

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [isMobile, getEffectiveHeaderWidth]);

  // 通知関連
  const hasExpiringSoon = !!(creditsData?.expiring_soon && creditsData.expiring_soon.amount > 0);

  // usodeki: ホーム + 素材パネルだけのモバイル特化構成。ヘッダー/フッターを出さない
  const showChrome = brandConfig.features.appChrome;

  return (
    <div className={`app-shell ${isAdminPage ? 'admin-mode' : ''}${location.pathname === '/' ? ' app-shell--home' : ''}${impersonationSession ? ' impersonating' : ''}${showChrome ? '' : ' app-shell--chromeless'}`}>
      <ScrollToTop />
      {/* <MobileNotice /> */}
      {impersonationSession && (
        <ImpersonationBanner session={impersonationSession} onEnd={endImpersonation} />
      )}
      {!isAdminPage && showChrome && (
      <header ref={headerRef} className="app-header">
        <Link to="/" className="brand-logo">
          <img src={brandConfig.logoWhite} alt={brandConfig.serviceName} />
        </Link>

        {!hideMainNav && (
          <MainNav
            hasNotification={hasExpiringSoon}
            expiringSoon={creditsData?.expiring_soon}
            isAdmin={isAdmin}
            isLoggedIn={!!token}
            onLogout={handleLogout}
            creditsBalance={creditsData ? (creditsData.total_available ?? creditsData.credits_balance) : null}
          />
        )}

        {/* モバイル用ヘルプアイコン + ポイント表示 */}
        {isMobile && (
          <div className="mobile-header-right" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ImpersonationOrgSwitcher />
            {brandConfig.features.helpCenter && (
            <a
              href={appPath('/manual')}
              target="_blank"
              rel="noopener noreferrer"
              className="icon-button"
              aria-label="ヘルプ"
              title="ヘルプ"
            >
              <img src="/question_circle.svg" alt="ヘルプ" width={20} height={20} style={{ opacity: 0.7 }} />
            </a>
            )}
            {creditsData && (
              <div className="mobile-points-wrapper">
                <Link to="/dashboard" className="mobile-points-display" onClick={() => close()}>
                  <span className="mobile-points-number">
                    {Math.floor(creditsData.total_available ?? creditsData.credits_balance).toLocaleString()}
                  </span>
                  <span className="mobile-points-unit">pt</span>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* PC用のヘッダーアクション（スマホ時はフッターに移動） */}
        {!isMobile && (
          <nav className="header-actions">
            <ImpersonationOrgSwitcher />
            {brandConfig.features.helpCenter && (
            <a
              href={appPath('/manual')}
              target="_blank"
              rel="noopener noreferrer"
              className="icon-button"
              aria-label="ヘルプ"
              title="ヘルプ"
            >
              <img src="/question_circle.svg" alt="ヘルプ" width={20} height={20} style={{ opacity: 0.7 }} />
            </a>
            )}
            <NotificationBell />
            <div className="pc-points-wrapper">
              {showPoints && creditsData && !isHeaderNarrow && (
                <Link to="/dashboard" className="points-display" onClick={() => close()}>
                  <span className="points-number">
                    {Math.floor(creditsData.total_available ?? creditsData.credits_balance).toLocaleString()}
                  </span>
                  <span className="points-unit">ポイント</span>
                </Link>
              )}
            </div>
            <div ref={menuRef}>
              <button
                type="button"
                className="icon-button menu-icon-button"
                data-guide-step="header-menu"
                onClick={() => setMenuOpen((prev) => !prev)}
                aria-label="メニュー"
              >
                <img src="/navigation_menu.svg" alt="メニュー" className="menu-icon" />
              </button>
              {isMenuOpen && (
                <div className="modal-surface menu-dropdown">
                  <MenuContent
                    isAdmin={isAdmin}
                    isLoggedIn={!!token}
                    onLogout={handleLogout}
                    showNavLinks={hideMainNav}
                  />
                </div>
              )}
            </div>
          </nav>
        )}
      </header>
      )}

      <main className="app-main">
        <Outlet />
      </main>

      {!isAdminPage && showChrome && (
        <footer className="app-footer">
          <span>&copy; {brandConfig.companyName}</span>
        </footer>
      )}

      {/* 生成ステータスバー（全ルートで表示） */}
      {activeJobCount > 0 && (
        <div
          className="generation-status-bar"
          onClick={() => setShowJobQueueModal(true)}
        >
          <div className="generation-status-inner">
            <span className="generation-status-pulse" />
            <span className="generation-status-text">
              {activeJobCount}件の生成が進行中
            </span>
          </div>
          <NotifyToggleSwitch variant="statusbar" />
        </div>
      )}

      {/* 属性収集モーダル（1問ずつ表示） */}
      <AttributeCollectionModal
        question={attributeCurrentQuestion ?? 'user_type'}
        isOpen={!!attributeCurrentQuestion}
        surveyConfig={surveyConfigQuery.data ?? null}
        isSubmitting={submitAttributeMutation.isPending}
        creditsGranted={submitAttributeMutation.data?.credits_granted ?? null}
        onAnswer={(question, answer) => submitAttributeMutation.mutate({ question, answer })}
        onSkip={() => dismissAttributesMutation.mutate()}
      />

      {/* 特急モード */}
      <ExpressModeModal
        isOpen={isExpressModeOpen}
        onClose={() => { setExpressModeOpen(false); setInitialAsin(null); }}
        initialAsin={initialAsin}
        onInitialAsinConsumed={() => setInitialAsin(null)}
      />
    </div>
  );
}
